import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Package, Clock, CheckCircle2, MapPin } from "lucide-react";
import ServicePrice from "../components/ServicePrice";
import { useCatalog } from "../contexts/CatalogContext";
import { API } from "../lib/api";

const STEPS = [
  { key: "confirmed", label: "Booking Confirmed", icon: CheckCircle2 },
  { key: "assigned", label: "Technician Assigned", icon: Package },
  { key: "ontheway", label: "On The Way", icon: MapPin },
  { key: "completed", label: "Job Done", icon: CheckCircle2 },
];

export default function BookingTrack() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getCategoryById, SLOTS, CITIES } = useCatalog();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/bookings/${id}`);
        setBooking(res.data);
      } catch (e) {
        // Unknown booking_id — UI shows "Booking not found" below.
      }
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div className="max-w-xl mx-auto py-20 text-center text-slate-500">Loading…</div>;
  if (!booking) return <div className="max-w-xl mx-auto py-20 text-center text-slate-500">Booking not found.</div>;

  const normalizeStatus = (status) => {
    const key = String(status || "").toLowerCase().trim().replace(/\s+/g, "_");
    const aliases = {
      en_route: "on_the_way",
      "en-route": "on_the_way",
      arrived: "on_the_way",
      on_the_way: "on_the_way",
      accepted: "accepted",
      assigned: "assigned",
      confirmed: "confirmed",
      work_started: "work_started",
      inspected: "inspected",
      completed: "completed",
    };
    return aliases[key] || key;
  };

  const getStatusLabel = (status) => {
    const labels = {
      confirmed: "Confirmed",
      assigned: "Technician Assigned",
      accepted: "Accepted",
      on_the_way: "On The Way",
      work_started: "Work Started",
      inspected: "Inspected",
      completed: "Completed",
    };
    return labels[normalizeStatus(status)] || status;
  };

  const activeIdx = {
    confirmed: 0,
    assigned: 1,
    accepted: 1,
    on_the_way: 2,
    work_started: 2,
    inspected: 2,
    completed: 3,
  }[normalizeStatus(booking.status)] ?? 0;

  const cat = getCategoryById(booking.category);
  const slotLbl = SLOTS.find((s) => s.id === booking.slot)?.label || booking.slot;
  const cityLbl = CITIES.find((c) => c.id === booking.city)?.name || booking.city;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="booking-track-page">
      <div className="text-center mb-8">
        <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Track Booking</span>
        <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-2">#{booking.booking_id}</h1>
        <p className="text-sm text-slate-500 mt-1">{booking.service_name} • {booking.brand}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-7">
        <div className="mb-6 flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3">
          <span className="text-sm text-slate-500">Current status</span>
          <span className="text-sm font-semibold text-slate-900">{getStatusLabel(booking.status)}</span>
        </div>

        <ol className="relative border-l-2 border-slate-100 ml-4 space-y-7">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i <= activeIdx;
            return (
              <li key={s.key} className="ml-6" data-testid={`track-step-${s.key}`}>
                <span className={`absolute -left-[14px] flex items-center justify-center w-7 h-7 rounded-full ${done ? "bg-[#0C4A6E] text-white" : "bg-slate-100 text-slate-400"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="font-semibold text-slate-900">{s.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {done ? "Completed" : "Pending"}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-8 pt-6 border-t border-slate-100 text-sm space-y-2">
          <div className="flex justify-between"><span className="text-slate-500">Slot</span><span className="font-semibold">{booking.date} • {slotLbl}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Address</span><span className="font-semibold text-right">{booking.address}, {cityLbl}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Estimated charge</span><span className="font-semibold"><ServicePrice service={booking} /></span></div>
        </div>
      </div>

      <div className="text-center mt-6">
        <Link to="/" className="text-sm font-semibold text-[#0C4A6E] hover:text-[#0369A1]" data-testid="track-back-home">← Back to home</Link>
      </div>
    </div>
  );
}
